import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { useT } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Building2,
  AlertCircle,
  CheckCircle,
  Clock,
  TrendingUp,
  Users,
  Package,
  FileWarning,
  Wrench,
  Activity,
  ArrowRight,
  Wallet,
  Landmark,
  Receipt,
  HandCoins
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

import RoleDashboard from './RoleDashboard';
import TenantDashboard from './TenantDashboard';

const ROLE_DASHBOARD_ROLES = ['TECHNICIAN', 'CS_SUPERVISOR', 'ENGINEER', 'ACCOUNTANT', 'STOREKEEPER', 'PROCUREMENT_OFFICER', 'HR_MANAGER', 'SECURITY_OFFICER'];

export default function DashboardRouter() {
  const { user } = useContext(AuthContext);
  // Phase 33 — tenants get a focused, complaint-centric dashboard.
  if (user?.role === 'TENANT') {
    return <TenantDashboard />;
  }
  // Phase 28 — route specialist roles to their dedicated workbench.
  if (user && ROLE_DASHBOARD_ROLES.includes(user.role)) {
    return <RoleDashboard role={user.role} />;
  }
  return <Dashboard />;
}

function Dashboard() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const t = useT();
  const [stats, setStats] = useState(null);
  const [complaints, setComplaints] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [utilityReadings, setUtilityReadings] = useState([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState('all');
  const [occupancy, setOccupancy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [financeKpis, setFinanceKpis] = useState(null);
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [glSummary, setGlSummary] = useState(null);  // journal-entry driven BS/P&L
  const [mrSummary, setMrSummary] = useState(null);  // pending material requests for storekeepers

  const canSeeFinance = ['FULL_ACCESS', 'ADMIN', 'CEO', 'ACCOUNTANT', 'SUPERADMIN'].includes(user?.role);
  const canSeeMr = ['STOREKEEPER', 'ENGINEER', 'MANAGER', 'FULL_ACCESS', 'ADMIN', 'SUPERADMIN'].includes(user?.role);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // refetch utility + stats when building filter changes
    const bldParam = selectedBuildingId === 'all' ? '' : `?building_id=${selectedBuildingId}`;
    axios.get(`${API}/utility-readings${bldParam}`, { withCredentials: true })
      .then(r => setUtilityReadings(r.data))
      .catch(() => {});
    axios.get(`${API}/dashboard/stats${bldParam}`, { withCredentials: true })
      .then(r => setStats(r.data))
      .catch(() => {});
  }, [selectedBuildingId]);

  const fetchData = async () => {
    try {
      const [statsRes, complaintsRes, buildingsRes, occRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
        axios.get(`${API}/complaints`, { withCredentials: true }),
        axios.get(`${API}/buildings`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/dashboard/occupancy`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data);
      setComplaints(complaintsRes.data);
      setBuildings(buildingsRes.data);
      setOccupancy(occRes.data);
      // Finance tiles — only fetched for roles that can see them; errors ignored (endpoint is role-gated).
      if (canSeeFinance) {
        const [kpiRes, bsRes, glRes] = await Promise.all([
          axios.get(`${API}/finance/kpis`, { withCredentials: true }).catch(() => ({ data: null })),
          axios.get(`${API}/finance/balance-sheet`, { withCredentials: true }).catch(() => ({ data: null })),
          axios.get(`${API}/accounting/dashboard-summary`, { withCredentials: true }).catch(() => ({ data: null })),
        ]);
        setFinanceKpis(kpiRes.data);
        setBalanceSheet(bsRes.data);
        setGlSummary(glRes.data);
      }
      // Material request summary — Storekeeper's pending-work tile
      if (canSeeMr) {
        const mrRes = await axios.get(`${API}/material-requests/summary/pending`, { withCredentials: true }).catch(() => ({ data: null }));
        setMrSummary(mrRes.data);
      }
    } catch (error) {
      toast.error('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Poll finance tiles every 30s so newly approved vouchers appear live
  useEffect(() => {
    if (!canSeeFinance) return undefined;
    const t = setInterval(async () => {
      try {
        const [kpiRes, bsRes, glRes] = await Promise.all([
          axios.get(`${API}/finance/kpis`, { withCredentials: true }),
          axios.get(`${API}/finance/balance-sheet`, { withCredentials: true }),
          axios.get(`${API}/accounting/dashboard-summary`, { withCredentials: true }),
        ]);
        setFinanceKpis(kpiRes.data);
        setBalanceSheet(bsRes.data);
        setGlSummary(glRes.data);
      } catch (e) { /* silent */ }
    }, 30000);
    return () => clearInterval(t);
  }, [canSeeFinance]);

  // Filter complaints by building
  const scopedComplaints = selectedBuildingId === 'all'
    ? complaints
    : complaints.filter(c => c.building_id === selectedBuildingId);

  // Prepare chart data (scoped by building filter)
  const complaintsByStatus = [
    { name: 'Open', value: scopedComplaints.filter(c => c.status === 'open').length, color: '#f59e0b' },
    { name: 'Assigned', value: scopedComplaints.filter(c => c.status === 'assigned').length, color: '#3b82f6' },
    { name: 'In Progress', value: scopedComplaints.filter(c => c.status === 'in_progress').length, color: '#8b5cf6' },
    { name: 'Resolved', value: scopedComplaints.filter(c => c.status === 'resolved').length, color: '#10b981' },
    { name: 'Closed', value: scopedComplaints.filter(c => c.status === 'closed').length, color: '#6b7280' }
  ];

  const complaintsByCategory = [
    { name: 'HVAC', value: scopedComplaints.filter(c => c.category === 'hvac').length },
    { name: 'Plumbing', value: scopedComplaints.filter(c => c.category === 'plumbing').length },
    { name: 'Electrical', value: scopedComplaints.filter(c => c.category === 'electrical').length },
    { name: 'Maintenance', value: scopedComplaints.filter(c => c.category === 'maintenance').length },
    { name: 'Security', value: scopedComplaints.filter(c => c.category === 'security').length },
    { name: 'Cleaning', value: scopedComplaints.filter(c => c.category === 'cleaning').length }
  ];

  // Per-building complaint volume (always shown — gives portfolio overview)
  const complaintsPerBuilding = buildings.map(b => ({
    name: b.name?.substring(0, 18) || '—',
    open: complaints.filter(c => c.building_id === b.id && c.status === 'open').length,
    inProgress: complaints.filter(c => c.building_id === b.id && c.status === 'in_progress').length,
    resolved: complaints.filter(c => c.building_id === b.id && (c.status === 'resolved' || c.status === 'closed')).length,
  }));

  // Utility consumption trend (last 6 months)
  const utilityTrend = utilityReadings
    .filter(r => r.utility_type === 'electricity')
    .sort((a, b) => new Date(a.reading_date) - new Date(b.reading_date))
    .slice(-6)
    .map(r => ({
      month: new Date(r.reading_date).toLocaleDateString('en-US', { month: 'short' }),
      value: r.reading_value
    }));

  const statCards = [
    { title: t('dash.totalBuildings'), value: stats?.total_buildings || 0, icon: Building2, bgColor: 'bg-emerald-100', iconColor: 'text-emerald-600', clickable: true, path: '/buildings' },
    { title: t('dash.openComplaints'), value: stats?.open_complaints || 0, icon: AlertCircle, bgColor: 'bg-red-100', iconColor: 'text-red-600', clickable: true, path: '/complaints?status=open' },
    { title: t('dash.inProgress'), value: stats?.in_progress_complaints || 0, icon: Clock, bgColor: 'bg-blue-100', iconColor: 'text-blue-600', clickable: true, path: '/complaints?status=in_progress' },
    { title: t('dash.resolvedToday'), value: stats?.resolved_complaints || 0, icon: CheckCircle, bgColor: 'bg-green-100', iconColor: 'text-green-600', clickable: true, path: '/complaints?status=resolved' },
    { title: t('dash.pendingPpm'), value: stats?.pending_ppm_tasks || 0, icon: Wrench, bgColor: 'bg-orange-100', iconColor: 'text-orange-600', clickable: true, path: '/ppm-tasks' },
    { title: t('dash.overdueTasks'), value: stats?.overdue_ppm_tasks || 0, icon: FileWarning, bgColor: 'bg-rose-100', iconColor: 'text-rose-600', clickable: true, path: '/ppm-tasks?filter=overdue' },
    { title: t('dash.lowStock'), value: stats?.low_stock_count || 0, icon: Package, bgColor: 'bg-amber-100', iconColor: 'text-amber-600', clickable: true, path: '/stock' },
    { title: t('dash.expiringCerts'), value: stats?.expiring_certificates || 0, icon: FileWarning, bgColor: 'bg-purple-100', iconColor: 'text-purple-600', clickable: true, path: '/certificates' }
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="dashboard-page" className="space-y-8 fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-1">
              {t('dash.welcome', { name: user?.name || '' })}
            </h1>
            <p className="text-base text-slate-600">
              {t('dash.subtitle')}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={selectedBuildingId} onValueChange={setSelectedBuildingId}>
              <SelectTrigger data-testid="dashboard-building-filter" className="w-56 bg-white dark:bg-slate-900/60 dark:text-slate-100 dark:border-slate-700">
                <SelectValue placeholder={t('common.allBuildings')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.allBuildings')}</SelectItem>
                {buildings.map(b => (
                  <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-right">
              <p className="text-xs text-muted-foreground">{t('common.role')}</p>
              <p className="text-sm font-semibold text-emerald-600">{user?.role}</p>
            </div>
          </div>
        </div>

        {/* Stats Grid — every card is clickable to drill into details */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card
                key={index}
                data-testid={`stat-card-${stat.title.toLowerCase().replace(/ /g, '-')}`}
                role="button"
                tabIndex={0}
                className={`p-6 transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 ${
                  stat.clickable ? 'cursor-pointer' : ''
                }`}
                onClick={() => stat.clickable && navigate(stat.path)}
                onKeyDown={(e) => { if (stat.clickable && (e.key === 'Enter' || e.key === ' ')) navigate(stat.path); }}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-xl ${stat.bgColor}`}>
                    <Icon className={`w-6 h-6 ${stat.iconColor}`} />
                  </div>
                  {stat.clickable && <ArrowRight className="w-4 h-4 text-slate-400" />}
                </div>
                <h3 className="text-sm font-medium text-slate-600 mb-1">{stat.title}</h3>
                <p className="text-3xl font-bold text-foreground">{stat.value}</p>
              </Card>
            );
          })}
        </div>

        {/* Finance Tiles — live BS / P&L numbers driven by journal entries.
            Only visible to finance-authorised roles. Auto-refreshes every 30s. */}
        {canSeeFinance && (
          <FinanceTiles kpis={financeKpis} balanceSheet={balanceSheet} glSummary={glSummary} onNavigate={navigate} />
        )}

        {/* Storekeeper / Engineer / Manager: material request queue shortcut */}
        {canSeeMr && mrSummary && (mrSummary.pending > 0 || mrSummary.ready_to_issue > 0) && (
          <Card
            data-testid="mr-shortcut-card"
            role="button"
            tabIndex={0}
            onClick={() => navigate('/material-requests')}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/material-requests'); }}
            className="p-6 cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl bg-gradient-to-br from-purple-50 via-white to-pink-50 border-2 border-purple-200"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg">
                  <Package className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Material request queue</h3>
                  <p className="text-sm text-slate-600">
                    <span data-testid="mr-pending-count" className="font-semibold text-rose-600">{mrSummary.pending}</span> awaiting approval
                    <span className="mx-2 text-slate-300">·</span>
                    <span data-testid="mr-ready-count" className="font-semibold text-emerald-600">{mrSummary.ready_to_issue}</span> ready to issue
                  </p>
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-purple-600" />
            </div>
          </Card>
        )}

        {/* Charts Grid — scoped by selected building (or whole portfolio) */}
        <div className="grid lg:grid-cols-2 gap-6">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-foreground">{t('dash.complaintsByStatus')}</h2>
              <span className="text-xs text-muted-foreground">
                {selectedBuildingId === 'all' ? t('common.portfolio') : (buildings.find(b => b.id === selectedBuildingId)?.name || '—')}
              </span>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={complaintsByStatus}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {complaintsByStatus.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-foreground">{t('dash.complaintsByCategory')}</h2>
              <span className="text-xs text-muted-foreground">
                {selectedBuildingId === 'all' ? t('common.portfolio') : (buildings.find(b => b.id === selectedBuildingId)?.name || '—')}
              </span>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={complaintsByCategory}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#10b981" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>

        {/* Per-building tenant occupancy — Feb-17 (client request) */}
        {occupancy?.buildings?.length > 0 && (
          <Card data-testid="occupancy-card" className="p-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div>
                <h2 className="text-xl font-bold text-foreground flex items-center gap-2"><Users className="w-5 h-5 text-teal-600" />Building-wise Occupancy</h2>
                <p className="text-xs text-muted-foreground mt-0.5">Occupied units, active leases &amp; monthly rent roll — per property.</p>
              </div>
              <div className="flex gap-3 flex-wrap">
                {occupancy.totals?.occupancy_pct !== null && occupancy.totals?.occupancy_pct !== undefined && (
                  <div data-testid="occ-total-pct" className="px-3 py-1.5 rounded-lg bg-teal-50 border border-teal-200">
                    <div className="text-[10px] uppercase font-semibold text-teal-700">Portfolio Occupancy</div>
                    <div className="text-lg font-bold text-teal-900">{occupancy.totals.occupancy_pct}%</div>
                    <div className="text-[10px] text-teal-700">{occupancy.totals.occupied}/{occupancy.totals.total_units} units</div>
                  </div>
                )}
                <div className="px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200">
                  <div className="text-[10px] uppercase font-semibold text-emerald-700">Monthly Rent Roll</div>
                  <div data-testid="occ-rent-roll" className="text-lg font-bold text-emerald-900">AED {(occupancy.totals?.monthly_rent_total || 0).toLocaleString()}</div>
                </div>
                {occupancy.totals?.expiring_soon > 0 && (
                  <div data-testid="occ-expiring" className="px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-200">
                    <div className="text-[10px] uppercase font-semibold text-amber-700">Renewals due (30d)</div>
                    <div className="text-lg font-bold text-amber-900">{occupancy.totals.expiring_soon}</div>
                  </div>
                )}
              </div>
            </div>

            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={occupancy.buildings.map(b => ({
                name: (b.building_name || '—').substring(0, 18),
                occupied: b.occupied_units,
                vacant: b.vacant_units || 0,
                pct: b.occupancy_pct,
              }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={70} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="occupied" stackId="u" fill="#14b8a6" name="Occupied" radius={[0, 0, 0, 0]} />
                <Bar dataKey="vacant" stackId="u" fill="#e2e8f0" name="Vacant" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>

            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase text-slate-500">
                    <th className="px-2 py-2">Building</th>
                    <th className="px-2 py-2 text-right">Units</th>
                    <th className="px-2 py-2 text-right">Occupied</th>
                    <th className="px-2 py-2 text-right">Vacant</th>
                    <th className="px-2 py-2 text-right">Occupancy</th>
                    <th className="px-2 py-2 text-right">Monthly rent</th>
                    <th className="px-2 py-2 text-right">Renewals&nbsp;30d</th>
                  </tr>
                </thead>
                <tbody>
                  {occupancy.buildings.map(b => (
                    <tr key={b.building_id} data-testid={`occ-row-${b.building_id}`} className="border-b last:border-0 hover:bg-slate-50">
                      <td className="px-2 py-2 font-medium">{b.building_name}</td>
                      <td className="px-2 py-2 text-right">{b.total_units ?? <span className="text-slate-400 italic">not set</span>}</td>
                      <td className="px-2 py-2 text-right">{b.occupied_units}</td>
                      <td className="px-2 py-2 text-right">{b.vacant_units ?? '—'}</td>
                      <td className="px-2 py-2 text-right">
                        {b.occupancy_pct !== null ? (
                          <span className={`px-1.5 py-0.5 rounded font-semibold ${b.occupancy_pct >= 90 ? 'bg-emerald-100 text-emerald-800' : b.occupancy_pct >= 60 ? 'bg-amber-100 text-amber-800' : 'bg-rose-100 text-rose-800'}`}>{b.occupancy_pct}%</span>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-2 py-2 text-right">{b.monthly_rent_total ? b.monthly_rent_total.toLocaleString() : '—'}</td>
                      <td className="px-2 py-2 text-right">{b.expiring_soon > 0 ? <span className="text-amber-700 font-semibold">{b.expiring_soon}</span> : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-[10px] text-muted-foreground mt-2">
                <em>Set <code>total_units</code> on a building (Buildings → Edit) to see the occupancy percentage.</em>
              </p>
            </div>

            {occupancy.expiring_leases?.length > 0 && (
              <div className="mt-4 border-t pt-3">
                <div className="text-xs font-semibold uppercase text-amber-700 mb-2">🔔 Lease renewals due in next 30 days</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {occupancy.expiring_leases.slice(0, 8).map(e => (
                    <div key={e.tenant_id} className="text-xs p-2 bg-amber-50 border border-amber-200 rounded flex items-center justify-between">
                      <span><strong>{e.tenant_name}</strong> · Unit {e.unit_number}</span>
                      <span className="font-mono text-amber-800">{e.days_left}d</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Per-building portfolio comparison — always visible */}
        {complaintsPerBuilding.length > 0 && (
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-foreground">{t('dash.perBuilding')}</h2>
              <span className="text-xs text-muted-foreground">{t('common.portfolio')}</span>
            </div>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={complaintsPerBuilding}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={70} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="open" stackId="a" fill="#f59e0b" name="Open" />
                <Bar dataKey="inProgress" stackId="a" fill="#8b5cf6" name="In Progress" />
                <Bar dataKey="resolved" stackId="a" fill="#10b981" name="Resolved/Closed" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* Utility Consumption Trend */}
        {utilityTrend.length > 0 && (
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-foreground">Electricity Consumption Trend</h2>
              <Activity className="w-5 h-5 text-slate-400" />
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={utilityTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value) => [`${value} kWh`, 'Consumption']} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={{ fill: '#3b82f6', r: 4 }}
                  name="Electricity (kWh)"
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* Quick Actions */}
        {user?.role !== 'TENANT' && (
          <Card className="p-6">
            <h2 className="text-2xl font-bold text-foreground mb-6">Quick Actions</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <button
                data-testid="quick-action-new-complaint"
                onClick={() => (window.location.href = '/complaints')}
                className="p-4 bg-gradient-to-br from-emerald-50 to-emerald-100 hover:from-emerald-100 hover:to-emerald-200 rounded-xl transition-all duration-300 text-left group"
              >
                <AlertCircle className="w-8 h-8 text-emerald-600 mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold text-foreground">New Complaint</h3>
                <p className="text-sm text-muted-foreground">Register new issue</p>
              </button>
              
              <button
                data-testid="quick-action-add-asset"
                onClick={() => (window.location.href = '/assets')}
                className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 rounded-xl transition-all duration-300 text-left group"
              >
                <Wrench className="w-8 h-8 text-blue-600 mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold text-foreground">Add Asset</h3>
                <p className="text-sm text-muted-foreground">Register new asset</p>
              </button>
              
              <button
                data-testid="quick-action-schedule-task"
                onClick={() => (window.location.href = '/ppm-tasks')}
                className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 hover:from-amber-100 hover:to-amber-200 rounded-xl transition-all duration-300 text-left group"
              >
                <Clock className="w-8 h-8 text-amber-600 mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold text-foreground">Schedule Task</h3>
                <p className="text-sm text-muted-foreground">Create PPM task</p>
              </button>
              
              <button
                data-testid="quick-action-ai-insights"
                onClick={() => (window.location.href = '/ai-insights')}
                className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 hover:from-purple-100 hover:to-purple-200 rounded-xl transition-all duration-300 text-left group"
              >
                <TrendingUp className="w-8 h-8 text-purple-600 mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold text-foreground">AI Insights</h3>
                <p className="text-sm text-muted-foreground">Get recommendations</p>
              </button>
            </div>
          </Card>
        )}

        {/* Pending Approvals */}
        {['MANAGER', 'SUPERVISOR'].includes(user?.role) && stats?.pending_purchase_requests > 0 && (
          <Card className="p-6 bg-gradient-to-br from-orange-50 to-orange-100 border-2 border-orange-300">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-foreground mb-1">Pending Approvals</h3>
                <p className="text-muted-foreground">
                  You have {stats.pending_purchase_requests} purchase requests awaiting approval
                </p>
              </div>
              <button
                data-testid="view-pending-approvals-btn"
                onClick={() => (window.location.href = '/purchases')}
                className="px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-xl font-semibold transition-colors"
              >
                Review Now
              </button>
            </div>
          </Card>
        )}
      </div>
    </Layout>
  );
}

// ── Finance tiles: live P&L + Balance-Sheet snapshot driven by journal entries.
function FinanceTiles({ kpis, balanceSheet, glSummary, onNavigate }) {
  const fmt = (n) => {
    if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
    return `AED ${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  };
  // Prefer JE-derived numbers (glSummary) — that is what the user asked for.
  // Fall back to invoice/bill KPIs if the GL summary is unavailable.
  const g = glSummary || {};
  const netProfit = g.net_profit_ytd ?? kpis?.net_profit ?? 0;
  const cash = g.cash ?? kpis?.cash_balance ?? 0;
  const ar = g.accounts_receivable ?? kpis?.accounts_receivable ?? 0;
  const ap = g.accounts_payable ?? kpis?.accounts_payable ?? 0;

  const tiles = [
    { title: 'Net Profit (YTD)', value: fmt(netProfit), icon: TrendingUp, accent: 'from-emerald-500 to-green-500', path: '/advanced-accounting', testid: 'finance-tile-net-profit' },
    { title: 'Cash Position',    value: fmt(cash),      icon: Wallet,     accent: 'from-teal-500 to-cyan-500',    path: '/vouchers',              testid: 'finance-tile-cash' },
    { title: 'AR Outstanding',   value: fmt(ar),        icon: Receipt,    accent: 'from-blue-500 to-indigo-500',  path: '/statements',            testid: 'finance-tile-ar' },
    { title: 'AP Outstanding',   value: fmt(ap),        icon: HandCoins,  accent: 'from-rose-500 to-red-500',     path: '/statements',            testid: 'finance-tile-ap' },
  ];

  // Merge JE totals into a synthesised balance-sheet view when the endpoint has data.
  const bs = glSummary
    ? {
        as_of: new Date().toISOString().slice(0, 10),
        assets: { cash: g.cash, accounts_receivable: g.accounts_receivable, inventory: g.inventory, fixed_assets: g.fixed_assets, total: g.total_assets },
        liabilities: { accounts_payable: g.accounts_payable, total: g.total_liabilities },
        equity: { retained: g.total_equity, total: g.total_equity },
      }
    : balanceSheet;
  const monthly = g.monthly_pnl || [];
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Landmark className="w-5 h-5 text-emerald-600" />
        <h2 className="text-xl font-bold text-foreground">Finance snapshot</h2>
        <span className="text-xs text-muted-foreground">
          Auto-refresh · from journal entries
          {g.journal_entry_count !== undefined && ` · ${g.journal_entry_count} JEs`}
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {tiles.map((t) => {
          const Icon = t.icon;
          return (
            <Card
              key={t.title}
              data-testid={t.testid}
              role="button"
              tabIndex={0}
              onClick={() => onNavigate(t.path)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onNavigate(t.path); }}
              className="relative p-6 cursor-pointer overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
            >
              <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${t.accent}`} />
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-xl bg-gradient-to-br ${t.accent} shadow-lg`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400" />
              </div>
              <h3 className="text-sm font-medium text-slate-600 mb-1">{t.title}</h3>
              <p className="text-2xl font-bold text-foreground">{t.value}</p>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {bs && (
          <Card data-testid="balance-sheet-mini" className="p-6 lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Balance sheet · as of {String(bs.as_of || '').slice(0, 10)}</h3>
              <button
                data-testid="view-full-bs-btn"
                onClick={() => onNavigate('/advanced-accounting')}
                className="text-xs text-emerald-600 hover:underline flex items-center gap-1"
              >
                Full report <ArrowRight className="w-3 h-3" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                <div className="text-emerald-700 font-semibold mb-2">Assets</div>
                <ul className="space-y-1 text-slate-600">
                  <li className="flex justify-between"><span>Cash</span><span className="font-medium">{fmt(bs.assets?.cash)}</span></li>
                  <li className="flex justify-between"><span>Receivables</span><span className="font-medium">{fmt(bs.assets?.accounts_receivable)}</span></li>
                  <li className="flex justify-between"><span>Inventory</span><span className="font-medium">{fmt(bs.assets?.inventory)}</span></li>
                  <li className="flex justify-between"><span>Fixed assets</span><span className="font-medium">{fmt(bs.assets?.fixed_assets)}</span></li>
                  <li className="flex justify-between pt-2 border-t border-emerald-200 mt-2 text-emerald-800 font-bold"><span>Total</span><span>{fmt(bs.assets?.total)}</span></li>
                </ul>
              </div>
              <div className="p-4 bg-rose-50 rounded-xl border border-rose-100">
                <div className="text-rose-700 font-semibold mb-2">Liabilities</div>
                <ul className="space-y-1 text-slate-600">
                  <li className="flex justify-between"><span>Payables</span><span className="font-medium">{fmt(bs.liabilities?.accounts_payable)}</span></li>
                  <li className="flex justify-between pt-2 border-t border-rose-200 mt-2 text-rose-800 font-bold"><span>Total</span><span>{fmt(bs.liabilities?.total)}</span></li>
                </ul>
              </div>
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                <div className="text-blue-700 font-semibold mb-2">Equity</div>
                <ul className="space-y-1 text-slate-600">
                  <li className="flex justify-between"><span>Retained</span><span className="font-medium">{fmt(bs.equity?.retained)}</span></li>
                  <li className="flex justify-between pt-2 border-t border-blue-200 mt-2 text-blue-800 font-bold"><span>Total</span><span>{fmt(bs.equity?.total)}</span></li>
                </ul>
              </div>
            </div>
          </Card>
        )}
        {monthly.length > 0 && (
          <Card data-testid="pnl-mini-chart" className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">P&amp;L · last 6 months</h3>
              <button
                data-testid="view-full-pnl-btn"
                onClick={() => onNavigate('/advanced-accounting')}
                className="text-xs text-emerald-600 hover:underline flex items-center gap-1"
              >
                Full P&amp;L <ArrowRight className="w-3 h-3" />
              </button>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => `AED ${Number(v).toLocaleString()}`} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="revenue" fill="#10b981" radius={[6, 6, 0, 0]} />
                <Bar dataKey="expense" fill="#ef4444" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}
      </div>
    </div>
  );
}